import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDataProvider, useNotify, useResourceContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useDebouncedValue } from '@mantine/hooks';
import { InfiniteData } from '@tanstack/react-query';
import {
  GetBatchEditPreflightParams,
  PostBatchEditParams,
  FullEditorialAsset,
} from 'utility/types/dataProvider';
import { ValidListFilterType, PartialVLF } from 'components/Search/types';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { useSearchItemsSafe } from 'components/Search/components/SearchItemsProvider';
import { useSearchSafe } from 'components/Search/SearchContextProvider';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { TGetListResult } from 'providers/contexts/extensions/functions/RequestBuilders';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';

type BatchEditKey = keyof ValidListFilterType;
type BatchEditValues = Record<BatchEditKey, InputChoice[]>;

const DEFAULT_VALUES: BatchEditValues = {
  sections: [],
  keywords: [],
  geo_tags: [],
  site_tags: [],
  flags: [],
  authors: [],
  property_no_publish: [],
  property_article_type: [],
  workflow_process: [],
  archive_time: [],
  delete_time: [],
  start_time: [],
  presentation_article: [],
  presentation_image: [],
  presentation_collection: [],
  photo_sales: [],
  property_priority: [],
  property_print_slug: [],
  property_byline: [],
  tag_keyword: [],
};

const SINGLE_SELECT_KEYS: BatchEditKey[] = [
  'property_no_publish',
  'workflow_process',
  'archive_time',
  'delete_time',
  'start_time',
  'presentation_article',
  'presentation_image',
  'presentation_collection',
  'property_article_type',
  'photo_sales',
  'property_priority',
  'property_print_slug',
  'property_byline',
];

const DATE_SELECT_KEYS: BatchEditKey[] = ['archive_time', 'delete_time', 'start_time'];

/** Maximum number of items that can be sent to the batch edit preflight API */
const MAX_PREFLIGHT_SELECTION_LIMIT = 50;

const API_TO_INTERNAL_MAP: Record<string, BatchEditKey> = {
  section: 'sections',
  keywords: 'keywords',
  tag_geo: 'geo_tags',
  tag_site: 'site_tags',
  flag: 'flags',
  authors: 'authors',
  no_publish: 'property_no_publish',
};

interface BatchEditContextType {
  dialogOpen: boolean;
  toggleDialog: (search?: boolean) => void;
  addRemoveValues: BatchEditValues;
  simpleValues: Partial<ValidListFilterType>;
  setAddRemoveValues: React.Dispatch<React.SetStateAction<BatchEditValues>>;
  updateAddRemoveValue: (
    group: BatchEditKey,
    value: string,
    action: 'add' | 'remove' | null,
  ) => void;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  modifySearchResults: boolean;
  setModifySearchResults: React.Dispatch<React.SetStateAction<boolean>>;
  handleSelectionChange: (key: BatchEditKey, newValues: string | string[]) => void;
  handleClearSelection: (key: BatchEditKey) => void;
  confirmationOpen: boolean;
  setConfirmationOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modifySearchResultsConfirm: boolean;
  setModifySearchResultsConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether the batch edit provider is in standalone mode (not within search context) */
  isStandaloneMode: boolean;
  /** Whether the current search is filtering for deleted assets */
  isDeletedSearch: boolean;
  /** The effective item IDs being used for batch operations (converted to string[]) */
  effectiveItemIds: string[];
  /** Total count of items available (for confirmation dialog) */
  totalItemCount: number;
}

/**
 * Props for BatchEditContextProvider
 * When used outside of search context, provide itemIds directly.
 * When used within search context, these are optional and will be
 * derived from SearchItemsProvider and SearchContextProvider.
 */
export interface BatchEditContextProviderProps {
  children: React.ReactNode;
  /** Optional: Provide UUIDs directly instead of using useSearchItems() */
  itemIds?: GridRowSelectionModel;
  /** Optional: Total count of items for confirmation dialog */
  totalCount?: number;
  /** Optional: Provide a resource name if not using useResourceContext() */
  resource?: string;
}

export const BatchEditContext = createContext<BatchEditContextType | undefined>(
  undefined,
);

export const useBatchEdit = () => {
  const context = useContext(BatchEditContext);
  if (!context)
    throw new Error('useBatchEdit must be used within BatchEditContextProvider');
  return context;
};

const createInputChoice = (value: string): InputChoice => ({
  label: value,
  value,
  misc: { established: true },
});

type BatchOperation = PostBatchEditParams['batch'];

export const BatchEditContextProvider = ({
  children,
  itemIds: externalItemIds,
  totalCount: externalTotalCount,
  resource: externalResource,
}: BatchEditContextProviderProps) => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const { t } = useTranslation();
  const resourceContext = useResourceContext();
  const { queryClient } = useCosmosQueryClient();

  // Use safe hooks that don't throw when outside search context
  const searchContext = useSearchSafe();
  const searchItemsContext = useSearchItemsSafe();

  // Determine if we're in standalone mode (no search context available)
  const isStandaloneMode = !searchContext || !searchItemsContext;

  // Use external props when provided, otherwise fall back to search context
  const resource = externalResource ?? resourceContext ?? 'editorial';
  const validListFilter = searchContext?.validListFilter ?? ({} as ValidListFilterType);

  // Memoize selectedItemIds to prevent unnecessary re-renders
  // Convert GridRowSelectionModel to string[] for internal use
  const selectedItemIds = useMemo(() => {
    const model = externalItemIds ?? searchItemsContext?.selectedItemIds;
    if (!model) return [];
    
    // Handle v8 format: { type: 'include' | 'exclude', ids: Set<GridRowId> }
    if (typeof model === 'object' && 'type' in model && 'ids' in model) {
      if (model.type === 'exclude') {
        throw new Error(
          'BatchEdit does not support exclude selection model. Use include mode only.',
        );
      }
      // Convert Set to array and stringify
      return Array.from(model.ids).map((id) => String(id));
    }
    
    // Fallback for legacy array format (shouldn't happen in v8)
    return [];
  }, [externalItemIds, searchItemsContext?.selectedItemIds]);

  // Use a ref to always have access to the latest selectedItemIds in callbacks
  // This prevents stale closure issues in useCallback
  const selectedItemIdsRef = useRef(selectedItemIds);
  useEffect(() => {
    selectedItemIdsRef.current = selectedItemIds;
  }, [selectedItemIds]);

  // Memoize totalItemCount for the confirmation dialog
  const totalItemCount = useMemo(
    () =>
      externalTotalCount ??
      searchItemsContext?.total ??
      (Array.isArray(selectedItemIds) ? selectedItemIds.length : 0),
    [externalTotalCount, searchItemsContext?.total, selectedItemIds],
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [addRemoveValues, setAddRemoveValues] = useState<BatchEditValues>(DEFAULT_VALUES);
  const [modifySearchResults, setModifySearchResults] = useState(false);
  const [modifySearchResultsConfirm, setModifySearchResultsConfirm] = useState(false);
  const [simpleValues, setSimpleValues] = useState<Partial<ValidListFilterType>>({});
  const [debouncedListFilter] = useDebouncedValue(validListFilter, 400);

  const {
    sort: debouncedSort,
    dir: debouncedOrder,
    ...debouncedFilter
  } = debouncedListFilter;

  // Reconstruct full filter with sort/dir
  const sanitizedFilter = useMemo(() => {
    return {
      ...debouncedFilter,
      sort: debouncedSort,
      dir: debouncedOrder,
    };
  }, [debouncedFilter, debouncedSort, debouncedOrder]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (modifySearchResults && !modifySearchResultsConfirm) {
        setConfirmationOpen(true);
        setDialogOpen(false);
        return;
      }

      const batchActions: BatchOperation = [];
      const presentationPayload: Record<string, string> = {};

      Object.entries(addRemoveValues).forEach(([field, choices]) => {
        const addValues = choices
          .filter((choice) => choice?.misc?.action === 'add')
          .map((choice) => choice?.value);

        const removeValues = choices
          .filter((choice) => choice?.misc?.action === 'remove')
          .map((choice) => choice?.value);

        // Special merge for presentation types
        if (
          field === 'presentation_article' ||
          field === 'presentation_image' ||
          field === 'presentation_collection'
        ) {
          const mediaType = field.replace('presentation_', '');
          if (addValues.length === 1) {
            presentationPayload[mediaType] = addValues[0];
          } else if (removeValues.length === 1) {
            presentationPayload[mediaType] = '';
          }
          return;
        }

        if (addValues.length > 0) {
          // Handle special case for workflow_process
          if (field === 'workflow_process' && addValues.length === 1) {
            const val = addValues[0];
            if (val === 'remove') {
              // Clear workflow fields
              batchActions.push(
                { op: 'add', field: 'property_workflow_id', value: '' },
                { op: 'add', field: 'property_workflow_process_id', value: '' },
              );
            } else if (val.includes('_')) {
              // Split and push workflow ID and process ID
              const [workflow_id, workflow_process_id] = val.split('_');
              batchActions.push(
                { op: 'add', field: 'property_workflow_id', value: workflow_id },
                {
                  op: 'add',
                  field: 'property_workflow_process_id',
                  value: workflow_process_id,
                },
              );
            }
          } else {
            const isSingle = SINGLE_SELECT_KEYS.includes(field as BatchEditKey);
            batchActions.push({
              op: 'add',
              field,
              value:
                // Handle special case for property_no_publish
                field === 'property_no_publish' && addValues.length === 1
                  ? addValues[0] === '1'
                    ? 1
                    : ''
                  : isSingle
                    ? addValues[0]
                    : addValues,
            });
          }
        }

        if (removeValues.length > 0) {
          batchActions.push({
            op: 'remove',
            field: field,
            value: removeValues,
          });
        }
      });

      if (Object.keys(presentationPayload).length > 0) {
        batchActions.push({
          op: 'add',
          field: 'presentation',
          value: JSON.stringify(presentationPayload),
        });
      }

      const payload: PostBatchEditParams = {
        app: resource,
        name: `${resource}-${
          modifySearchResults ? 'search-results' : 'selected-assets'
        }-batch-edit`,
        search: modifySearchResults
          ? sanitizedFilter
          : { uuids: selectedItemIdsRef.current },
        batch: batchActions,
      };

      try {
        const response = await dataProvider.createBatchEditTask(payload);
        if (response?.status === 'success') {
          notify(t('blox.batch_edit.help.success'), { type: 'success' });
          setDialogOpen(false);
          setModifySearchResults(false);
          setModifySearchResultsConfirm(false);
          setConfirmationOpen(false);

          // Optimistically remove items from search cache when undoing deletion
          // on a deleted-asset search view. The batch task runs async on the server,
          // so we remove items locally to give immediate visual feedback.
          const isUndoingDeletion =
            validListFilter?.deleted === '1' &&
            batchActions.some(
              (action) =>
                action.op === 'add' &&
                action.field === 'delete_time' &&
                action.value === '',
            );

          if (isUndoingDeletion) {
            const affectedIds = new Set<string>(
              modifySearchResults ? [] : selectedItemIdsRef.current,
            );
            const shouldRemoveAll = modifySearchResults;

            const filterItems = (items: Partial<FullEditorialAsset>[]) =>
              shouldRemoveAll
                ? []
                : items.filter((item) => !item.uuid || !affectedIds.has(item.uuid));

            queryClient.setQueriesData<any>(
              { queryKey: [resource, 'getList'] },
              (oldData: TGetListResult<Partial<FullEditorialAsset>>) => {
                if (!oldData?.data) return oldData;
                const filtered = filterItems(oldData.data);
                const removedCount = oldData.data.length - filtered.length;
                return {
                  ...oldData,
                  data: filtered,
                  total: Math.max(0, (oldData.total ?? 0) - removedCount),
                };
              },
            );

            queryClient.setQueriesData<any>(
              { queryKey: [resource, 'getInfiniteList'] },
              (oldData: InfiniteData<TGetListResult<Partial<FullEditorialAsset>>>) => {
                if (!oldData?.pages) return oldData;
                return {
                  ...oldData,
                  pages: oldData.pages.map(
                    (page: TGetListResult<Partial<FullEditorialAsset>>) => {
                      if (!page?.data) return page;
                      const filtered = filterItems(page.data);
                      const removedCount = page.data.length - filtered.length;
                      return {
                        ...page,
                        data: filtered,
                        total: Math.max(0, (page.total ?? 0) - removedCount),
                      };
                    },
                  ),
                };
              },
            );

            queryClient.invalidateQueries({ queryKey: [resource], refetchType: 'none' });
          }
        } else {
          // Handle non-success responses
          notify(
            t('blox.batch_edit.errors.submitFailed', {
              message: response?.message || '',
            }),
            { type: 'error' },
          );
        }
      } catch (error: any) {
        console.error('Error creating batch edit task:', {
          error,
          message: error?.message,
          details: error?.details,
        });
        notify(
          t('blox.batch_edit.errors.submitFailed', {
            message: error?.message || error?.axiosMessage || '',
          }),
          { type: 'error' },
        );
      }
    },
    [
      addRemoveValues,
      dataProvider,
      resource,
      modifySearchResults,
      modifySearchResultsConfirm,
      sanitizedFilter,
      validListFilter?.deleted,
      queryClient,
      t,
      notify,
    ],
  );

  const toggleDialog = useCallback(
    async (search = false) => {
      // Use ref to get the latest selectedItemIds value (avoids stale closure)
      const currentSelectedIds = selectedItemIdsRef.current;

      // Block dialog from opening if selection exceeds limit (when not in search mode)
      if (!search && currentSelectedIds.length > MAX_PREFLIGHT_SELECTION_LIMIT) {
        notify(
          t('blox.batch_edit.help.selectionExceedsLimit', {
            count: MAX_PREFLIGHT_SELECTION_LIMIT,
          }),
          { type: 'error', autoHideDuration: 8000 },
        );
        return;
      }

      if (!dialogOpen) {
        const payload = {
          app: resource,
          name: 'batchEditPreflight',
          search: search ? sanitizedFilter : { uuids: currentSelectedIds },
          batch: [],
        } as GetBatchEditPreflightParams;

        try {
          const response = await dataProvider.getBatchEditPreflight(payload);
          if (response?.status === 'success' && response.data?.field_values) {
            const combinedValues = { ...DEFAULT_VALUES };

            Object.entries(response.data.field_values).forEach(([apiKey, values]) => {
              if (!Array.isArray(values)) return;

              const internalKey = API_TO_INTERNAL_MAP[apiKey];
              if (!internalKey) return;

              combinedValues[internalKey] =
                apiKey === 'authors'
                  ? values?.map((author: any) => {
                      const firstName = author.first_name || '';
                      const lastName = author.last_name || '';
                      const email = author.email || '';
                      const accountIsAdmin = author.account_type === 'admin';
                      const prefixAdminAccount = accountIsAdmin ? '(Admin)' : '';
                      return {
                        label: `${prefixAdminAccount} ${firstName} ${lastName} `,
                        sublabel: `${email}`,
                        value: author.uuid,
                        misc: { established: true },
                      };
                    })
                  : values?.map((v) => createInputChoice(String(v)));
            });
            setModifySearchResults(search);
            setAddRemoveValues(combinedValues);
            setDialogOpen(true);
            return;
          }

          // Handle non-success responses
          notify(
            t('blox.batch_edit.errors.preflightFailed', {
              message: response?.message || '',
            }),
            { type: 'error' },
          );
        } catch (error: any) {
          console.error('Error during dialog toggle:', error);
          notify(
            t('blox.batch_edit.errors.preflightFailed', {
              message: error?.message || error?.axiosMessage || '',
            }),
            { type: 'error' },
          );
          return;
        }
      }
      setModifySearchResults(false);
      setModifySearchResultsConfirm(false);
      setDialogOpen(false);
    },
    [dialogOpen, dataProvider, sanitizedFilter, resource, notify, t],
  );

  const handleSelectionChange = useCallback(
    (key: BatchEditKey, newValues: string | string[]) => {
      setAddRemoveValues((prev) => {
        const prevSet = new Set(prev[key].map((x) => x.value));
        const newSet = new Set(Array.isArray(newValues) ? newValues : [newValues]);

        const isSingleSelectKey = SINGLE_SELECT_KEYS.includes(key);
        const selectedValue = Array.isArray(newValues) ? newValues[0] : newValues;

        if (isSingleSelectKey) {
          // Hanlde special case date remove
          const actualValue =
            DATE_SELECT_KEYS.includes(key) && selectedValue === '__REMOVE__'
              ? ''
              : selectedValue;

          const wasSameValue = prev[key]?.[0]?.value === actualValue;
          return {
            ...prev,
            [key]: wasSameValue
              ? [] // toggle off if same value
              : [
                  {
                    value: actualValue,
                    label: selectedValue,
                    misc: { action: 'add' },
                  },
                ],
          };
        }

        // handle empty selection
        if (!newValues || (Array.isArray(newValues) && newValues.length === 0)) {
          // This is the case when all items are deselected

          // If there are no established values, we can just return an empty array for this key
          if (!prev[key].some((item) => item.misc?.established)) {
            return {
              ...prev,
              [key]: [],
            };
          }

          return {
            ...prev,
            [key]: prev[key]
              .map((item) => {
                if (item.misc?.established) {
                  // Toggle action for established items instead of just setting to 'remove'
                  const currentAction = item.misc.action;
                  const newAction =
                    currentAction === 'add'
                      ? 'remove'
                      : currentAction === 'remove'
                        ? null
                        : 'add';
                  return { ...item, misc: { ...item.misc, action: newAction } };
                }
                return item;
              })
              .filter((item) => item.misc?.established || item.misc?.action === 'add'),
          };
        }

        const changedValue =
          prev[key].find((x) => !newSet.has(x.value))?.value ||
          [...newSet].find((x) => !prevSet.has(x));

        const existingItem = prev[key].find((item) => item.value === changedValue);
        let updatedValues = prev[key].filter((item) => item.value !== changedValue);

        if (existingItem?.misc?.established) {
          const currentAction = existingItem.misc.action;
          const newAction =
            currentAction === 'add'
              ? 'remove'
              : currentAction === 'remove'
                ? null
                : 'add';
          const updatedItem = {
            ...existingItem,
            misc: { ...existingItem.misc, action: newAction },
          };
          updatedValues.push(updatedItem);
        } else if (!existingItem && newSet.has(changedValue || '')) {
          updatedValues.push({
            value: changedValue || '',
            label: changedValue || '',
            misc: { action: 'add' },
          });
        }

        return {
          ...prev,
          [key]: updatedValues,
        };
      });
    },
    [setAddRemoveValues],
  );

  const handleClearSelection = useCallback(
    (key: BatchEditKey) => {
      setAddRemoveValues((prev) => {
        return {
          ...prev,
          [key]: prev[key]
            .filter((item) => {
              // Keep only established items, remove all non-established items
              return item.misc?.established;
            })
            .map((item) => {
              // Mark all established items for removal
              return {
                ...item,
                misc: {
                  ...item.misc,
                  action: 'remove',
                },
              };
            }),
        };
      });
    },
    [setAddRemoveValues],
  );

  useEffect(() => {
    const transformedValues = Object.entries(addRemoveValues).reduce<PartialVLF>(
      (acc, [key, choices]) => ({
        ...acc,
        [key]: choices
          .filter(
            (choice) =>
              choice?.misc?.established ||
              (!choice?.misc?.established && choice?.misc?.action === 'add'),
          )
          .map((choice) => choice.value),
      }),
      {
        sections: [],
        keywords: [],
        geo_tags: [],
        site_tags: [],
        flags: [],
        authors: [],
      },
    );

    setSimpleValues(transformedValues);
  }, [addRemoveValues]);

  // Update add/remove values for a specific group
  const updateAddRemoveValue = useCallback(
    (group: BatchEditKey, value: string, action: 'add' | 'remove' | null) => {
      setAddRemoveValues((prev) => {
        const groupItems = prev[group] || [];

        if (action === null) {
          // Remove the item entirely from the group
          return {
            ...prev,
            [group]: groupItems.filter((item) => item.value !== value),
          };
        }

        const itemIndex = groupItems.findIndex((item) => item.value === value);

        const newItem = {
          value,
          label: value,
          misc: {
            ...groupItems[itemIndex]?.misc,
            action,
            established: false,
          },
        };

        // Replace existing item if found
        if (itemIndex >= 0) {
          const updatedItems = [...groupItems];
          updatedItems[itemIndex] = newItem;
          return {
            ...prev,
            [group]: updatedItems,
          };
        }

        // Otherwise, add new item
        return {
          ...prev,
          [group]: [...groupItems, newItem],
        };
      });
    },
    [],
  );

  return (
    <BatchEditContext.Provider
      value={{
        dialogOpen,
        toggleDialog,
        addRemoveValues,
        simpleValues,
        setAddRemoveValues,
        updateAddRemoveValue,
        handleSubmit,
        modifySearchResults,
        setModifySearchResults,
        handleSelectionChange,
        handleClearSelection,
        confirmationOpen,
        setConfirmationOpen,
        modifySearchResultsConfirm,
        setModifySearchResultsConfirm,
        isStandaloneMode,
        isDeletedSearch: validListFilter?.deleted === '1',
        effectiveItemIds: selectedItemIds,
        totalItemCount,
      }}
    >
      {children}
    </BatchEditContext.Provider>
  );
};
